/**
 * StakeholderFormDialog
 * Reusable dialog for creating and editing Investors, Brokers, Collateral
 * Providers and Issuers. When editing an investor it shows Details / Contracts /
 * Documents tabs; when editing an issuer it shows Details / Programs / Documents
 * (Programs replace Contracts, and documents are grouped by program).
 * Extracted from StakeholderManagement page.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiPut, extractApiError } from '../../utils/apiClient';
import logger from '../../utils/logger';
import { useTenant } from '../../contexts/TenantContext';
import useAsyncData from '../../hooks/useAsyncData';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from './ConfirmDialog';
import DataTable from './DataTable';
import StakeholderDocumentsTab from './StakeholderDocumentsTab';
import { a11yProps } from './TabPanel';
import { formatDate, formatCurrency } from '../../utils/formatters';
import { getStatusColor } from '../../utils/statusColors';

// ─── Helper: collapsible section ─────────────────────────────────────────────
const CollapsibleSection = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <>
      <Grid size={12}>
        <Box
          onClick={() => setOpen(!open)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            mt: 2,
            mb: 0.5,
            '&:hover': { bgcolor: 'action.hover', borderRadius: 1 },
            px: 0.5,
            py: 0.25,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            {title}
          </Typography>
          {open ? <ExpandLessIcon fontSize="small" color="action" /> : <ExpandMoreIcon fontSize="small" color="action" />}
        </Box>
        <Divider />
      </Grid>
      {open && children}
    </>
  );
};

// Renders sub-tab content when active, nothing when inactive. react-hook-form's
// default shouldUnregister=false preserves field values across mount/unmount.
const SubTabSection = ({ active, children }) => (active ? <>{children}</> : null);

// Keep SectionHeader for non-collapsible usage
const SectionHeader = ({ children }) => (
  <Grid size={12}>
    <Typography
      variant="subtitle2"
      sx={{ mt: 2, mb: 1, fontWeight: 'bold', color: 'text.primary' }}
    >
      {children}
    </Typography>
    <Divider />
  </Grid>
);

// ─── Default form values per stakeholder type ────────────────────────────────
const investorDefaults = {
  name: '',
  type: '',
  domicile_country_id: '',
  currency_id: '',
  email: '',
  wht_rate: 0,
  wht_gross_up: false,
  wht_rate_override: false,
  wht_override_reason: '',
  wht_override_approved_by: '',
  is_related_party: false,
  // T3-131: this investor also acts as a broker. Included here so the reset() merge below
  // hydrates it from the API record (fn_stakeholder_investor_get_by_id returns it).
  acts_as_broker: false,
  title: '',
  first_name: '',
  surname: '',
  multinational_flag: false,
  active: true,
  document_upload: null,
  // Identity & Classification
  client_code: '',
  id_type: '',
  id_number: '',
  profession: '',
  is_staff: false,
  client_segment: '',
  tax_classification: '',
  tax_residency_country_id: '',
  // Banking Details
  bank_name: '',
  bank_account_number: '',
  bank_branch_code: '',
  bank_account_type: '',
  bank_reference: '',
  // Contact Details
  physical_address: '',
  postal_address: '',
  secondary_email: '',
  secondary_phone: '',
  preferred_contact_method: '',
  // KYC/AML
  fica_status: '',
  fica_verified_date: '',
  fica_expiry_date: '',
  kyc_risk_rating: '',
  pep_status: false,
  sanctions_screened: false,
  sanctions_screened_date: '',
  last_review_date: '',
  next_review_date: '',
  // Relationship Management
  relationship_manager: '',
  source_referral: '',
  date_onboarded: '',
  notes: '',
  // Commission Override
  commission_override_enabled: false,
  commission_rate_override: '',
  commission_override_reason: '',
  commission_override_approved_by: '',
};

const brokerDefaults = {
  name: '',
  type: '',
  domicile_country_id: '',
  currency_id: '',
  email: '',
  active: true,
  phone: '',
  address: '',
  contact_person: '',
  // Commission Structure
  default_commission_rate: '',
  commission_calculation_method: '',
  commission_payment_frequency: '',
  commission_currency_id: '',
  // Regulatory & Licensing
  license_number: '',
  regulatory_body: '',
  license_expiry_date: '',
  license_status: '',
  tax_number: '',
  registration_number: '',
  // Relationship
  relationship_start_date: '',
  notes: '',
  is_preferred: false,
};

const providerDefaults = {
  name: '',
  type: '',
  domicile_country_id: '',
  currency_id: '',
  active: true,
  phone: '',
  address: '',
  contact_person: '',
  // Credit & Capacity
  credit_rating: '',
  credit_rating_agency: '',
  credit_rating_date: '',
  total_exposure_limit: '',
  // Regulatory
  registration_number: '',
  tax_number: '',
  license_number: '',
  // Capability
  guarantee_fee_rate: '',
  relationship_start_date: '',
  notes: '',
};

const issuerDefaults = {
  name: '',
  type: '',
  site: '',
  domicile_country_id: '',
  currency_id: '',
  email: '',
  phone: '',
  address: '',
  contact_person: '',
  active: true,
};

// ─── Default notification preferences ────────────────────────────────────────
const defaultNotificationPrefs = {
  contract_approved: true,
  contract_rejected: true,
  payment_reminder: true,
  overdue_payment: true,
  maturity_reminder: true,
  rollover_confirmation: true,
  rate_change: true,
};

function getDefaultsByType(type) {
  if (type === 'investor') return investorDefaults;
  if (type === 'broker') return brokerDefaults;
  if (type === 'issuer') return issuerDefaults;
  return providerDefaults;
}

// ─── Main component ───────────────────────────────────────────────────────────
function StakeholderFormDialog({
  open,
  onClose,
  onSaved,
  stakeholderType,
  editingItem = null,
  countries = [],
  initialMode = 'edit',
}) {
  const { isFeatureEnabled } = useTenant();
  const navigate = useNavigate();
  const confirm = useConfirmDialog();

  const { control, handleSubmit, reset, watch, formState } = useForm();
  const isDirty = formState.isDirty;
  // react-hook-form keeps isSubmitting true for the duration of the async
  // onSubmit, so we use it to lock the save buttons against double-submit.
  const isSubmitting = formState.isSubmitting;

  const [notificationPrefs, setNotificationPrefs] = useState(defaultNotificationPrefs);
  const [initialPrefs, setInitialPrefs] = useState(defaultNotificationPrefs);
  const [saveError, setSaveError] = useState(null);
  const [mode, setMode] = useState(initialMode);
  const [tab, setTab] = useState(0);
  const [detailsSubTab, setDetailsSubTab] = useState(0);
  const [liveItem, setLiveItem] = useState(editingItem);
  const [loadingItem, setLoadingItem] = useState(false);

  const watchedType = watch('type');
  const watchedWhtOverride = watch('wht_rate_override');
  const watchedCommissionOverride = watch('commission_override_enabled');
  const watchedActsAsBroker = watch('acts_as_broker');

  const isInvestor = stakeholderType === 'investor';
  const isIssuer = stakeholderType === 'issuer';
  // Investors show Details/Contracts/Documents; issuers show Details/Programs/Documents.
  const showTabs = !!editingItem && (isInvestor || isIssuer);
  const readOnly = mode === 'view';
  const prefsDirty = useMemo(
    () => JSON.stringify(notificationPrefs) !== JSON.stringify(initialPrefs),
    [notificationPrefs, initialPrefs]
  );
  const formDirty = isDirty || prefsDirty;

  // Reset form when dialog opens, stakeholderType/editingItem changes, or
  // when the canonical record is re-fetched. Builds form values from liveItem
  // when available (full T041 record), falling back to editingItem (list row).
  useEffect(() => {
    if (!open) return;

    const defaults = getDefaultsByType(stakeholderType);
    const source = liveItem || editingItem;

    if (source) {
      const merged = {};
      Object.keys(defaults).forEach((key) => {
        merged[key] = source[key] !== undefined ? source[key] : defaults[key];
      });
      // Derive commission_override_enabled from whether a rate is set
      if (
        source.commission_rate_override != null &&
        source.commission_rate_override !== '' &&
        source.commission_rate_override !== 0
      ) {
        merged.commission_override_enabled = true;
      }
      reset(merged);

      // Load notification preferences from item, merging with defaults for any missing keys
      const savedPrefs =
        source.notification_preferences &&
        Object.keys(source.notification_preferences).length > 0
          ? { ...defaultNotificationPrefs, ...source.notification_preferences }
          : defaultNotificationPrefs;
      setNotificationPrefs(savedPrefs);
      setInitialPrefs(savedPrefs);
    } else {
      reset(defaults);
      setNotificationPrefs(defaultNotificationPrefs);
      setInitialPrefs(defaultNotificationPrefs);
    }

    setSaveError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stakeholderType, editingItem, liveItem, reset]);

  // Reset transient state (mode, tab, liveItem) when the dialog opens on a
  // different record, or when it closes.
  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setTab(0);
      setDetailsSubTab(0);
      setLiveItem(editingItem);
    }
  }, [open, editingItem?.id, initialMode]);  // eslint-disable-line react-hooks/exhaustive-deps

  // For existing investors/issuers, re-fetch the canonical record on open so the
  // form has the full field set (not just the list-row subset).
  useEffect(() => {
    if (!open || !(isInvestor || isIssuer) || !editingItem?.id) return;
    const endpoint = isInvestor
      ? `/stakeholders/investors/${editingItem.id}`
      : `/stakeholders/issuers/${editingItem.id}`;
    let cancelled = false;
    setLoadingItem(true);
    apiGet(endpoint)
      .then((res) => {
        if (!cancelled && res?.data) setLiveItem(res.data);
      })
      .catch((err) => {
        logger.error('Failed to fetch full stakeholder record', { message: err.message });
      })
      .finally(() => {
        if (!cancelled) setLoadingItem(false);
      });
    return () => { cancelled = true; };
  }, [open, isInvestor, isIssuer, editingItem?.id]);

  // Fetch contracts for the investor Contracts/Documents tabs (investor only).
  const stakeholderId = editingItem?.id;
  const fetchContracts = useCallback(async () => {
    if (!stakeholderId || !isInvestor) return [];
    const res = await apiGet(`/contracts?investor_id=${stakeholderId}&limit=100`);
    return res?.data || [];
  }, [stakeholderId, isInvestor]);

  const {
    data: contracts,
    loading: contractsLoading,
    error: contractsError,
  } = useAsyncData(fetchContracts, {
    defaultValue: [],
    fetchOnMount: open && showTabs && isInvestor && (tab === 1 || tab === 2) && !!stakeholderId,
    deps: [open, showTabs, tab, stakeholderId],
    extractData: false,
  });

  // Fetch programs for the issuer Programs/Documents tabs (issuer only). Pulls the
  // full portfolio (incl. inactive/expired) so retired programs that still own
  // documents remain visible.
  const fetchPrograms = useCallback(async () => {
    if (!stakeholderId || !isIssuer) return [];
    const res = await apiGet(`/refdata/programs?issuer_id=${stakeholderId}&all_for_issuer=true&active_only=false`);
    return res?.data || [];
  }, [stakeholderId, isIssuer]);

  const {
    data: programs,
    loading: programsLoading,
    error: programsError,
  } = useAsyncData(fetchPrograms, {
    defaultValue: [],
    fetchOnMount: open && showTabs && isIssuer && (tab === 1 || tab === 2) && !!stakeholderId,
    deps: [open, showTabs, tab, stakeholderId],
    extractData: false,
  });

  const guardedClose = () => {
    if (mode === 'edit' && formDirty) {
      confirm.show({
        title: 'Discard changes?',
        message: 'You have unsaved changes. Close without saving?',
        confirmText: 'Discard',
        confirmColor: 'error',
        onConfirm: () => {
          setSaveError(null);
          onClose();
        },
      });
      return;
    }
    setSaveError(null);
    onClose();
  };

  const handleTabChange = (_e, newTab) => {
    if (mode === 'edit' && formDirty && newTab !== tab) {
      confirm.show({
        title: 'Discard changes?',
        message: 'You have unsaved changes on this tab. Switch and discard?',
        confirmText: 'Discard',
        confirmColor: 'error',
        onConfirm: () => {
          // Revert form state by resetting from liveItem/editingItem, then switch tab
          const defaults = getDefaultsByType(stakeholderType);
          const source = liveItem || editingItem || {};
          const merged = {};
          Object.keys(defaults).forEach((k) => {
            merged[k] = source[k] !== undefined ? source[k] : defaults[k];
          });
          reset(merged);
          setNotificationPrefs(initialPrefs);
          setMode('view');
          setTab(newTab);
        },
      });
      return;
    }
    setTab(newTab);
  };

  const handleEnterEditMode = () => {
    setMode('edit');
    setTab(0);
  };

  const handleCancelEdit = () => {
    if (formDirty) {
      confirm.show({
        title: 'Discard changes?',
        message: 'You have unsaved changes. Discard them?',
        confirmText: 'Discard',
        confirmColor: 'error',
        onConfirm: () => {
          const defaults = getDefaultsByType(stakeholderType);
          const source = liveItem || editingItem || {};
          const merged = {};
          Object.keys(defaults).forEach((k) => {
            merged[k] = source[k] !== undefined ? source[k] : defaults[k];
          });
          reset(merged);
          setNotificationPrefs(initialPrefs);
          setMode('view');
        },
      });
      return;
    }
    setMode('view');
  };

  const handleContractClick = (contract) => {
    onClose();
    if (contract.status === 'Draft' || contract.status === 'Pending Approval') {
      navigate(`/contracts/${contract.id}/edit`);
    } else {
      navigate(`/contracts/${contract.id}`);
    }
  };

  const contractColumns = [
    {
      key: 'reference',
      header: 'Reference',
      sortable: true,
      getValue: (c) => c.user_reference || c.reference,
      render: (c) => (
        <Typography variant="body2" fontWeight="medium">
          {c.user_reference || c.reference}
        </Typography>
      ),
    },
    {
      key: 'product_type',
      header: 'Product',
      sortable: true,
      getValue: (c) => c.product_type,
      render: (c) => (c.product_type ? <Chip label={c.product_type} size="small" variant="outlined" /> : '-'),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      getValue: (c) => c.status,
      render: (c) => <Chip label={c.status} size="small" color={getStatusColor(c.status)} />,
    },
    {
      key: 'start_date',
      header: 'Start Date',
      sortable: true,
      getValue: (c) => c.start_date,
      render: (c) => formatDate(c.start_date),
    },
    {
      key: 'capital_amount',
      header: 'Capital Amount',
      align: 'right',
      sortable: true,
      getValue: (c) => (c.capital_amount != null ? parseFloat(c.capital_amount) : 0),
      render: (c) => formatCurrency(c.capital_amount, c.currency_code || 'ZAR'),
    },
    {
      key: 'interest_rate',
      header: 'Rate',
      align: 'right',
      sortable: true,
      getValue: (c) => (c.interest_rate != null ? parseFloat(c.interest_rate) : 0),
      render: (c) =>
        c.interest_rate != null ? `${parseFloat(c.interest_rate).toFixed(2)}%` : '-',
    },
  ];

  const isProgramExpired = (p) =>
    p.expiry_date && new Date(p.expiry_date) < new Date(new Date().toDateString());

  const programColumns = [
    {
      key: 'name',
      header: 'Program',
      sortable: true,
      getValue: (p) => p.name,
      render: (p) => (
        <Typography variant="body2" fontWeight="medium">{p.name}</Typography>
      ),
    },
    {
      key: 'currency_code',
      header: 'Currency',
      sortable: true,
      getValue: (p) => p.currency_code,
      render: (p) => p.currency_code || '-',
    },
    {
      key: 'limit_amount',
      header: 'Limit',
      align: 'right',
      sortable: true,
      getValue: (p) => (p.limit_amount != null ? parseFloat(p.limit_amount) : 0),
      render: (p) =>
        p.limit_amount != null ? formatCurrency(p.limit_amount, p.currency_code || 'ZAR') : 'No limit',
    },
    {
      key: 'used_capital',
      header: 'Used',
      align: 'right',
      sortable: true,
      getValue: (p) => (p.used_capital != null ? parseFloat(p.used_capital) : 0),
      render: (p) => formatCurrency(p.used_capital || 0, p.currency_code || 'ZAR'),
    },
    {
      key: 'available',
      header: 'Available',
      align: 'right',
      sortable: true,
      getValue: (p) => (p.available != null ? parseFloat(p.available) : 0),
      render: (p) =>
        p.available != null ? formatCurrency(p.available, p.currency_code || 'ZAR') : '-',
    },
    {
      key: 'listed',
      header: 'Listed',
      sortable: true,
      getValue: (p) => (p.listed ? 1 : 0),
      render: (p) => <Chip label={p.listed ? 'Yes' : 'No'} size="small" variant="outlined" />,
    },
    {
      key: 'active',
      header: 'Status',
      sortable: true,
      getValue: (p) => (p.active ? 1 : 0),
      render: (p) =>
        isProgramExpired(p) ? (
          <Chip label="Expired" size="small" color="warning" />
        ) : (
          <Chip
            label={p.active ? 'Active' : 'Inactive'}
            size="small"
            color={p.active ? 'success' : 'default'}
          />
        ),
    },
  ];

  // Performs the actual create/update request. Split out from onSubmit so the
  // duplicate-warning flow can re-invoke it with confirm_duplicate=true after
  // the user chooses to override.
  const persistStakeholder = async (data, { confirmDuplicate = false } = {}) => {
    let endpoint = '';
    if (stakeholderType === 'investor') endpoint = '/stakeholders/investors';
    if (stakeholderType === 'broker') endpoint = '/stakeholders/brokers';
    if (stakeholderType === 'provider') endpoint = '/stakeholders/collateral-providers';
    if (stakeholderType === 'issuer') endpoint = '/stakeholders/issuers';

    const payload = {
      ...data,
      notification_preferences: notificationPrefs,
      ...(confirmDuplicate ? { confirm_duplicate: true } : {}),
    };

    if (editingItem) {
      await apiPut(`${endpoint}/${editingItem.id}`, payload);
      // For investors/issuers viewed in the merged dialog, stay open and flip
      // back to view mode with refreshed data. Otherwise close (legacy behaviour
      // for brokers/collateral, and any edit that came in without tabs context).
      if (showTabs) {
        try {
          const refetchEndpoint = isInvestor
            ? `/stakeholders/investors/${editingItem.id}`
            : `/stakeholders/issuers/${editingItem.id}`;
          const res = await apiGet(refetchEndpoint);
          if (res?.data) setLiveItem(res.data);
        } catch (refetchErr) {
          logger.error('Failed to refresh stakeholder after save', { message: refetchErr.message });
        }
        setMode('view');
        onSaved({ id: editingItem.id, ...payload });
      } else {
        onSaved({ id: editingItem.id, ...payload });
        onClose();
      }
    } else {
      const response = await apiPost(endpoint, payload);
      const newId = response?.data?.id ?? response?.id ?? null;
      onSaved({ id: newId, ...payload });
      // Create flow: close the dialog automatically (the parent used to do
      // this; the merged dialog now owns its close lifecycle).
      onClose();
    }
  };

  // Detects the soft duplicate-warning the API raises (409 + DuplicateWarning).
  const getDuplicateWarning = (err) => {
    if (err?.response?.status !== 409) return null;
    const details = err?.response?.data?.error?.details;
    return details?.type === 'DuplicateWarning' ? details : null;
  };

  const onSubmit = async (data) => {
    setSaveError(null);

    // For Individual investors, concatenate title + first_name + surname into name
    if (stakeholderType === 'investor' && data.type === 'Individual') {
      const parts = [data.title, data.first_name, data.surname].filter(Boolean).map(s => s.trim()).filter(Boolean);
      data.name = parts.join(' ');
    }

    try {
      await persistStakeholder(data);
    } catch (err) {
      const dupWarning = getDuplicateWarning(err);
      if (dupWarning) {
        const names = (dupWarning.candidates || [])
          .map((c) => `• ${c.name}${c.active === false ? ' (inactive)' : ''}`)
          .join('\n');
        // Word the prompt for the actual flow: on edit we are updating an existing record,
        // not creating one, so "Save anyway" is accurate; on create it is genuinely a create.
        const isEdit = Boolean(editingItem);
        confirm.show({
          title: 'Possible duplicate investor',
          message: isEdit
            ? `A separate investor with this name already exists:\n${names}\n\nSave anyway?`
            : `An investor with this name already exists:\n${names}\n\nCreate this record anyway?`,
          confirmText: isEdit ? 'Save anyway' : 'Create anyway',
          confirmColor: 'warning',
          onConfirm: async () => {
            try {
              await persistStakeholder(data, { confirmDuplicate: true });
            } catch (retryErr) {
              logger.error('Error saving stakeholder after duplicate override', { message: retryErr.message });
              setSaveError(extractApiError(retryErr, 'An unexpected error occurred while saving.'));
            }
          },
        });
        return;
      }
      logger.error('Error saving stakeholder', { message: err.message });
      setSaveError(extractApiError(err, 'An unexpected error occurred while saving.'));
    }
  };

  // ─── Dialog form content ──────────────────────────────────────────────────
  // For investors, sections are rendered as horizontal sub-tabs instead of
  // collapsible accordions. Build the enabled-section list dynamically so
  // feature-flagged sections drop out of the strip entirely.
  const investorSubTabs = isInvestor
    ? [
        { key: 'basic', label: 'Basic', enabled: true },
        { key: 'identity', label: 'Identity', enabled: isFeatureEnabled('stakeholder_management', 'investors.identity_classification') },
        { key: 'banking', label: 'Banking', enabled: isFeatureEnabled('stakeholder_management', 'investors.banking_details') },
        { key: 'contact', label: 'Contact', enabled: isFeatureEnabled('stakeholder_management', 'investors.contact_details') },
        { key: 'kyc', label: 'KYC / AML', enabled: isFeatureEnabled('stakeholder_management', 'investors.kyc_aml') },
        { key: 'relationship', label: 'Relationship', enabled: isFeatureEnabled('stakeholder_management', 'investors.relationship_management') },
        { key: 'wht', label: 'WHT', enabled: isFeatureEnabled('stakeholder_management', 'investors.wht_override') },
        { key: 'commission', label: 'Commission', enabled: isFeatureEnabled('stakeholder_management', 'investors.commission_override') },
        { key: 'notifications', label: 'Notifications', enabled: true },
      ].filter((s) => s.enabled)
    : [];
  const investorSubTabIndex = (key) => investorSubTabs.findIndex((s) => s.key === key);
  const isInvestorSubTabActive = (key) => {
    const idx = investorSubTabIndex(key);
    return idx === -1 ? false : detailsSubTab === idx;
  };

  const renderNotificationPrefsContent = () => (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Select which notifications this {stakeholderType} should receive:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              setNotificationPrefs({
                contract_approved: true,
                contract_rejected: true,
                payment_reminder: true,
                overdue_payment: true,
                maturity_reminder: true,
                rollover_confirmation: true,
                rate_change: true,
              })
            }
          >
            All
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              setNotificationPrefs({
                contract_approved: false,
                contract_rejected: false,
                payment_reminder: false,
                overdue_payment: false,
                maturity_reminder: false,
                rollover_confirmation: false,
                rate_change: false,
              })
            }
          >
            None
          </Button>
        </Box>
      </Box>
      <FormGroup>
        <Grid container spacing={1}>
          {[
            { key: 'contract_approved', label: 'Contract Approved' },
            { key: 'contract_rejected', label: 'Contract Rejected' },
            { key: 'payment_reminder', label: 'Payment Reminder' },
            { key: 'overdue_payment', label: 'Overdue Payment' },
            { key: 'maturity_reminder', label: 'Maturity Reminder' },
            { key: 'rollover_confirmation', label: 'Rollover Confirmation' },
            { key: 'rate_change', label: 'Rate Change' },
          ].map(({ key, label }) => (
            <Grid
              key={key}
              size={{
                xs: 12,
                sm: 6,
                md: 4
              }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationPrefs[key]}
                    onChange={(e) =>
                      setNotificationPrefs({ ...notificationPrefs, [key]: e.target.checked })
                    }
                    size="small"
                  />
                }
                label={label}
              />
            </Grid>
          ))}
        </Grid>
      </FormGroup>
    </>
  );

  const renderDialogForm = () => (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* Sub-tab strip is rendered outside the disabled fieldset so it remains
          clickable in view mode (a disabled fieldset blocks all controls inside). */}
      {isInvestor && investorSubTabs.length > 1 && (
        <Tabs
          value={Math.min(detailsSubTab, investorSubTabs.length - 1)}
          onChange={(_e, v) => setDetailsSubTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
        >
          {investorSubTabs.map((s, i) => (
            <Tab key={s.key} label={s.label} {...a11yProps(i, 'stakeholder-details')} />
          ))}
        </Tabs>
      )}
      <fieldset
        disabled={readOnly}
        style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
      >
      <Grid container spacing={2} sx={{ mt: 0.5 }}>

        {saveError && (
          <Grid size={12}>
            <Alert severity="error" onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          </Grid>
        )}

        <SubTabSection active={!isInvestor || isInvestorSubTabActive('basic')}>
        {/* ── Core fields (all types) ───────────────────────────── */}
        {!isInvestor && <SectionHeader>Basic Information</SectionHeader>}

        {/* ══════════════════════════════════════════════════════════════════════════
            TWO-COLUMN LAYOUT: data entry on the LEFT, every toggle on the RIGHT.

            Previously the fields and switches shared one flowing 2-column grid, so a
            toggle landed wherever the flow happened to put it — Related Party top-right,
            "also acts as a broker" bottom-left, Gross-Up mid-right, Active bottom-left.
            Switches were scattered across both columns and read as unrelated controls.

            Now each column is its OWN nested grid, so the two sides scroll independently
            and a toggle can never be pushed into the input column by a conditional field
            above it (e.g. Individual's Title/First/Surname, or the issuer Site).
            The old spacer Grids that padded the flow are gone with it.
        ══════════════════════════════════════════════════════════════════════════ */}

        {/* ── LEFT: data entry ─────────────────────────────────────── */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Grid container spacing={2}>

            {/* Type */}
            <Grid size={12}>
              <Controller
                name="type"
                control={control}
                defaultValue=""
                rules={{ required: 'Type is required' }}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Type *</InputLabel>
                    <Select {...field} label="Type *">
                      {stakeholderType === 'investor' && [
                        <MenuItem key="Bank" value="Bank">Bank</MenuItem>,
                        <MenuItem key="Company" value="Company">Company</MenuItem>,
                        <MenuItem key="Fund" value="Fund">Fund</MenuItem>,
                        <MenuItem key="Individual" value="Individual">Individual</MenuItem>,
                        <MenuItem key="Trust" value="Trust">Trust</MenuItem>,
                      ]}
                      {stakeholderType === 'broker' && [
                        <MenuItem key="Broker" value="Broker">Broker</MenuItem>,
                        <MenuItem key="Bank" value="Bank">Bank</MenuItem>,
                      ]}
                      {stakeholderType === 'provider' && [
                        <MenuItem key="Bank" value="Bank">Bank</MenuItem>,
                        <MenuItem key="Institution" value="Institution">Institution</MenuItem>,
                      ]}
                      {stakeholderType === 'issuer' && [
                        <MenuItem key="Bank" value="Bank">Bank</MenuItem>,
                        <MenuItem key="Company" value="Company">Company</MenuItem>,
                        <MenuItem key="Fund" value="Fund">Fund</MenuItem>,
                        <MenuItem key="Trust" value="Trust">Trust</MenuItem>,
                        <MenuItem key="Institution" value="Institution">Institution</MenuItem>,
                      ]}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Name: Individual gets Title + First + Surname, others an entity name */}
            {stakeholderType === 'investor' && watchedType === 'Individual' ? (
              <>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <Controller
                    name="title"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Title</InputLabel>
                        <Select {...field} label="Title">
                          <MenuItem value="">None</MenuItem>
                          <MenuItem value="Mr">Mr</MenuItem>
                          <MenuItem value="Mrs">Mrs</MenuItem>
                          <MenuItem value="Ms">Ms</MenuItem>
                          <MenuItem value="Dr">Dr</MenuItem>
                          <MenuItem value="Prof">Prof</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4.5 }}>
                  <Controller
                    name="first_name"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'First name is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField {...field} fullWidth label="First Name *" error={!!error} helperText={error?.message} />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4.5 }}>
                  <Controller
                    name="surname"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Surname is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField {...field} fullWidth label="Surname *" error={!!error} helperText={error?.message} />
                    )}
                  />
                </Grid>
              </>
            ) : (
              <Grid size={12}>
                <Controller
                  name="name"
                  control={control}
                  defaultValue=""
                  rules={{ required: 'Name is required' }}
                  render={({ field }) => (
                    <TextField {...field} fullWidth label="Entity Name *" />
                  )}
                />
              </Grid>
            )}

            {/* Site (issuer only) */}
            {stakeholderType === 'issuer' && (
              <Grid size={12}>
                <Controller
                  name="site"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: 'Site is required',
                    validate: (v) => (v && v.length === 5) || 'Site must be exactly 5 characters',
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Site *"
                      inputProps={{ maxLength: 5, style: { textTransform: 'uppercase' } }}
                      onChange={(e) => field.onChange((e.target.value || '').toUpperCase())}
                      error={!!error}
                      helperText={error?.message || '5-character accounting site code (used by journal export)'}
                    />
                  )}
                />
              </Grid>
            )}

            {/* Domicile Country + Currency (controlled by basic_information feature) */}
            {isFeatureEnabled('stakeholder_management', `${stakeholderType === 'investor' ? 'investors' : stakeholderType === 'broker' ? 'brokers' : stakeholderType === 'issuer' ? 'issuers' : 'collateral'}.basic_information`) && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="domicile_country_id"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Domicile country is required' }}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Domicile Country *</InputLabel>
                        <Select {...field} label="Domicile Country *">
                          {countries.map((country) => (
                            <MenuItem key={country.id} value={country.id}>
                              {country.country}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="currency_id"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Currency is required' }}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Currency *</InputLabel>
                        <Select {...field} label="Currency *">
                          {countries.map((country) => (
                            <MenuItem key={country.id} value={country.id}>
                              {country.currency_code} - {country.country}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </>
            )}

            {/* Email (investors, brokers, issuers) */}
            {(stakeholderType === 'investor' || stakeholderType === 'broker' || stakeholderType === 'issuer') && (
              <Grid size={12}>
                <Controller
                  name="email"
                  control={control}
                  defaultValue=""
                  rules={{
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Email Address"
                      type="email"
                      error={!!error}
                      helperText={error?.message || 'Used for email notifications'}
                    />
                  )}
                />
              </Grid>
            )}

            {/* WHT rate (investors only) — its Gross-Up toggle lives in the right column */}
            {stakeholderType === 'investor' && (
              <Grid size={12}>
                <Controller
                  name="wht_rate"
                  control={control}
                  defaultValue={0}
                  rules={{
                    min: { value: 0, message: 'WHT rate cannot be negative' },
                    max: { value: 100, message: 'WHT rate cannot exceed 100%' },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Withholding Tax Rate (%)"
                      type="number"
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      error={!!error}
                      helperText={error?.message || 'Withholding tax rate applied to interest payments'}
                    />
                  )}
                />
              </Grid>
            )}

          </Grid>
        </Grid>

        {/* ── RIGHT: every toggle, in one column ───────────────────── */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: 1, backgroundColor: 'action.hover' }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: 'block', mb: 1, lineHeight: 1.4 }}
            >
              Settings
            </Typography>

            {/* Vertical stack, so the divider is a plain horizontal rule — not `flexItem`,
                which is for dividers running across a row. */}
            <Stack divider={<Divider />} spacing={1.5}>

              {/* Active — first, because it is the one every type has */}
              <Box>
                <Controller
                  name="active"
                  control={control}
                  defaultValue={true}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value !== false}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Active"
                    />
                  )}
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Inactive records are hidden from selection lists.
                </Typography>
              </Box>

              {/* Related Party (investors only) */}
              {stakeholderType === 'investor' && (
                <Box>
                  <Controller
                    name="is_related_party"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value || false}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="Related Party"
                      />
                    )}
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Indicates whether this investor is a related party.
                  </Typography>
                </Box>
              )}

              {/* WHT Gross-Up (investors only) — pairs with the WHT rate on the left */}
              {stakeholderType === 'investor' && (
                <Box>
                  <Controller
                    name="wht_gross_up"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value || false}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="Withholding Tax Gross-Up"
                      />
                    )}
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    When enabled, WHT is a company expense. Investor receives full gross interest.
                  </Typography>
                </Box>
              )}

              {/* T3-131: acts-as-broker. Gated on the brokers module — with brokers switched
                  off for a tenant there is no broker picker for the linked record to appear
                  in, so the toggle would promise something the tenant cannot use. */}
              {stakeholderType === 'investor' && isFeatureEnabled('stakeholder_management', 'brokers') && (
                <Box>
                  <Controller
                    name="acts_as_broker"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value || false}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="Also acts as a broker"
                      />
                    )}
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    {watchedActsAsBroker
                      ? (liveItem?.linked_broker_name
                        ? `Available as broker "${liveItem.linked_broker_name}" on contracts.`
                        : 'A matching broker record will be created so this entity can be selected as a broker.')
                      : 'Creates a linked broker record so this entity is captured once, not twice.'}
                  </Typography>
                  {watchedActsAsBroker && liveItem?.linked_broker_id && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Turning this off deactivates that broker record. It is kept, not deleted,
                      and is restored if you switch this back on — but it cannot be turned off
                      while the broker is used on a contract.
                    </Typography>
                  )}
                </Box>
              )}

            </Stack>
          </Paper>
        </Grid>

        </SubTabSection>

        {/* ══════════════════════════════════════════════════════════
            INVESTOR-ONLY SECTIONS (rendered as sub-tabs)
        ══════════════════════════════════════════════════════════ */}
        {stakeholderType === 'investor' && (
          <>
            {/* ── Identity & Classification ─────────────────────── */}
            {isFeatureEnabled('stakeholder_management', 'investors.identity_classification') && (
            <SubTabSection active={isInvestorSubTabActive('identity')}>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="client_code"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Client Code" />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="id_type"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>ID Type</InputLabel>
                    <Select {...field} label="ID Type">
                      <MenuItem value="National ID">National ID</MenuItem>
                      <MenuItem value="Passport">Passport</MenuItem>
                      <MenuItem value="Drivers License">Drivers License</MenuItem>
                      <MenuItem value="Company Registration">Company Registration</MenuItem>
                      <MenuItem value="Trust Deed">Trust Deed</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="id_number"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField {...field} fullWidth label="ID Number" />
                )}
              />
            </Grid>

            {/* Profession — individual only */}
            {watchedType === 'Individual' && (
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="profession"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField {...field} fullWidth label="Profession" />
                  )}
                />
              </Grid>
            )}

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="client_segment"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Client Segment</InputLabel>
                    <Select {...field} label="Client Segment">
                      <MenuItem value="Retail">Retail</MenuItem>
                      <MenuItem value="High Net Worth">High Net Worth</MenuItem>
                      <MenuItem value="Institutional">Institutional</MenuItem>
                      <MenuItem value="Corporate">Corporate</MenuItem>
                      <MenuItem value="Staff">Staff</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="tax_classification"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Tax Classification</InputLabel>
                    <Select {...field} label="Tax Classification">
                      <MenuItem value="Individual">Individual</MenuItem>
                      <MenuItem value="Corporate">Corporate</MenuItem>
                      <MenuItem value="Foreign">Foreign</MenuItem>
                      <MenuItem value="Trust">Trust</MenuItem>
                      <MenuItem value="Exempt">Exempt</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="tax_residency_country_id"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Autocomplete
                    options={countries}
                    getOptionLabel={(o) => (typeof o === 'object' ? o.country : countries.find((c) => c.id === o)?.country || '')}
                    value={countries.find((c) => c.id === field.value) || null}
                    onChange={(_, newVal) => field.onChange(newVal ? newVal.id : '')}
                    renderInput={(params) => <TextField {...params} label="Tax Residency Country" />}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="is_staff"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value || false}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Is Staff Member"
                  />
                )}
              />
            </Grid>

            </SubTabSection>)}

            {/* ── Banking Details ───────────────────────────────── */}
            {isFeatureEnabled('stakeholder_management', 'investors.banking_details') && (
            <SubTabSection active={isInvestorSubTabActive('banking')}>

            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Controller
                name="bank_name"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="Bank Name" />}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Controller
                name="bank_account_number"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="Account Number" />}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="bank_branch_code"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="Branch Code" />}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="bank_account_type"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Account Type</InputLabel>
                    <Select {...field} label="Account Type">
                      <MenuItem value="Cheque">Cheque</MenuItem>
                      <MenuItem value="Savings">Savings</MenuItem>
                      <MenuItem value="Transmission">Transmission</MenuItem>
                      <MenuItem value="Trust">Trust</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="bank_reference"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="Bank Reference" />}
              />
            </Grid>

            </SubTabSection>)}

            {/* ── Contact Details ───────────────────────────────── */}
            {isFeatureEnabled('stakeholder_management', 'investors.contact_details') && (
            <SubTabSection active={isInvestorSubTabActive('contact')}>

            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Controller
                name="physical_address"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Physical Address" multiline rows={2} />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Controller
                name="postal_address"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Postal Address" multiline rows={2} />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="secondary_email"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Secondary Email" type="email" />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="secondary_phone"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Secondary Phone" />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="preferred_contact_method"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Preferred Contact Method</InputLabel>
                    <Select {...field} label="Preferred Contact Method">
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="phone">Phone</MenuItem>
                      <MenuItem value="post">Post</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            </SubTabSection>)}

            {/* ── KYC / AML Compliance ──────────────────────────── */}
            {isFeatureEnabled('stakeholder_management', 'investors.kyc_aml') && (
            <SubTabSection active={isInvestorSubTabActive('kyc')}>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="fica_status"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>FICA Status</InputLabel>
                    <Select {...field} label="FICA Status">
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="compliant">Compliant</MenuItem>
                      <MenuItem value="non_compliant">Non-Compliant</MenuItem>
                      <MenuItem value="expired">Expired</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="fica_verified_date"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="FICA Verified Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="fica_expiry_date"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="FICA Expiry Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="kyc_risk_rating"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>KYC Risk Rating</InputLabel>
                    <Select {...field} label="KYC Risk Rating">
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="last_review_date"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Last Review Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="next_review_date"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Next Review Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="pep_status"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value || false}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Politically Exposed Person (PEP)"
                  />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="sanctions_screened"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value || false}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Sanctions Screened"
                  />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="sanctions_screened_date"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Sanctions Screened Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            </SubTabSection>)}

            {/* ── Relationship Management ───────────────────────── */}
            {isFeatureEnabled('stakeholder_management', 'investors.relationship_management') && (
            <SubTabSection active={isInvestorSubTabActive('relationship')}>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="relationship_manager"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="Relationship Manager" />}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="source_referral"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="Source / Referral" />}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="date_onboarded"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Date Onboarded"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name="notes"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Notes" multiline rows={3} />
                )}
              />
            </Grid>

            </SubTabSection>)}

            {/* ── WHT Override ──────────────────────────────────── */}
            {isFeatureEnabled('stakeholder_management', 'investors.wht_override') && (
            <SubTabSection active={isInvestorSubTabActive('wht')}>

            <Grid size={12}>
              <Controller
                name="wht_rate_override"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value || false}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Override WHT Rate"
                  />
                )}
              />
            </Grid>

            {!watchedWhtOverride && editingItem?.resolved_wht_rate != null && (
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <TextField
                  fullWidth
                  label="Resolved WHT Rate"
                  value={`${parseFloat(editingItem.resolved_wht_rate).toFixed(4)}%`}
                  InputProps={{ readOnly: true }}
                  helperText={editingItem.rate_source ? `Source: ${editingItem.rate_source}` : undefined}
                />
              </Grid>
            )}

            {watchedWhtOverride && (
              <>
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <Controller
                    name="wht_rate"
                    control={control}
                    defaultValue={0}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Override WHT Rate (%)"
                        type="number"
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                      />
                    )}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <Controller
                    name="wht_override_reason"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField {...field} fullWidth label="Override Reason" />
                    )}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <Controller
                    name="wht_override_approved_by"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField {...field} fullWidth label="Approved By" />
                    )}
                  />
                </Grid>
              </>
            )}

            </SubTabSection>)}

            {/* ── Commission Override ───────────────────────────── */}
            {isFeatureEnabled('stakeholder_management', 'investors.commission_override') && (
            <SubTabSection active={isInvestorSubTabActive('commission')}>

            <Grid size={12}>
              <Controller
                name="commission_override_enabled"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value || false}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Override Commission Rate"
                  />
                )}
              />
            </Grid>

            {watchedCommissionOverride && (
              <>
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <Controller
                    name="commission_rate_override"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Commission Rate Override"
                        type="number"
                        inputProps={{ min: 0, step: 0.0001 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <Controller
                    name="commission_override_reason"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField {...field} fullWidth label="Override Reason" />
                    )}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <Controller
                    name="commission_override_approved_by"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField {...field} fullWidth label="Approved By" />
                    )}
                  />
                </Grid>
              </>
            )}

            </SubTabSection>)}
          </>
        )}

        {/* ══════════════════════════════════════════════════════════
            BROKER-ONLY SECTIONS
        ══════════════════════════════════════════════════════════ */}
        {stakeholderType === 'broker' && (
          <>
            {/* ── Contact Information ───────────────────────────── */}
            {isFeatureEnabled('stakeholder_management', 'brokers.contact_info') && (
            <CollapsibleSection title="Contact Information">

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="contact_person"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="Contact Person" />}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="phone"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="Phone" />}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              {/* spacer */}
            </Grid>

            <Grid size={12}>
              <Controller
                name="address"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Address" multiline rows={2} />
                )}
              />
            </Grid>
            </CollapsibleSection>)}

            {/* ── Commission Structure ──────────────────────────── */}
            {isFeatureEnabled('stakeholder_management', 'brokers.commission_structure') && (
            <CollapsibleSection title="Commission Structure">

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="default_commission_rate"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Default Commission Rate"
                    type="number"
                    inputProps={{ min: 0, step: 0.0001 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="commission_calculation_method"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Calculation Method</InputLabel>
                    <Select {...field} label="Calculation Method">
                      <MenuItem value="spread">Spread</MenuItem>
                      <MenuItem value="flat_fee">Flat Fee</MenuItem>
                      <MenuItem value="tiered">Tiered</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="commission_payment_frequency"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Payment Frequency" />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Controller
                name="commission_currency_id"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Autocomplete
                    options={countries}
                    getOptionLabel={(o) => (
                      typeof o === 'object'
                        ? `${o.currency_code} - ${o.country}`
                        : countries.find((c) => c.id === o)
                          ? `${countries.find((c) => c.id === o).currency_code} - ${countries.find((c) => c.id === o).country}`
                          : ''
                    )}
                    value={countries.find((c) => c.id === field.value) || null}
                    onChange={(_, newVal) => field.onChange(newVal ? newVal.id : '')}
                    renderInput={(params) => <TextField {...params} label="Commission Currency" />}
                  />
                )}
              />
            </Grid>

            </CollapsibleSection>)}

            {/* ── Regulatory & Licensing ────────────────────────── */}
            {isFeatureEnabled('stakeholder_management', 'brokers.regulatory_licensing') && (
            <CollapsibleSection title="Regulatory & Licensing">

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="license_number"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="License Number" />}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="regulatory_body"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="Regulatory Body" />}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="license_expiry_date"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="License Expiry Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="license_status"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>License Status</InputLabel>
                    <Select {...field} label="License Status">
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="suspended">Suspended</MenuItem>
                      <MenuItem value="expired">Expired</MenuItem>
                      <MenuItem value="revoked">Revoked</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="tax_number"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="Tax Number" />}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="registration_number"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="Registration Number" />}
              />
            </Grid>

            </CollapsibleSection>)}

            {/* ── Relationship ──────────────────────────────────── */}
            {isFeatureEnabled('stakeholder_management', 'brokers.relationship') && (
            <CollapsibleSection title="Relationship">

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="relationship_start_date"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Relationship Start Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="is_preferred"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value || false}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Preferred Broker"
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name="notes"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Notes" multiline rows={3} />
                )}
              />
            </Grid>
            </CollapsibleSection>)}
          </>
        )}

        {/* ══════════════════════════════════════════════════════════
            ISSUER-ONLY SECTIONS
        ══════════════════════════════════════════════════════════ */}
        {stakeholderType === 'issuer' && (
          <CollapsibleSection title="Contact Information">
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="contact_person"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="Contact Person" />}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Controller
                name="phone"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="Phone" />}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>{/* spacer */}</Grid>
            <Grid size={12}>
              <Controller
                name="address"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Address" multiline rows={2} />
                )}
              />
            </Grid>
          </CollapsibleSection>
        )}

        {/* ══════════════════════════════════════════════════════════
            COLLATERAL PROVIDER SECTIONS
        ══════════════════════════════════════════════════════════ */}
        {stakeholderType === 'provider' && (
          <>
            {/* ── Contact Information ───────────────────────────── */}
            {isFeatureEnabled('stakeholder_management', 'collateral.contact_info') && (
            <CollapsibleSection title="Contact Information">

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="contact_person"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="Contact Person" />}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="phone"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="Phone" />}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              {/* spacer */}
            </Grid>

            <Grid size={12}>
              <Controller
                name="address"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Address" multiline rows={2} />
                )}
              />
            </Grid>

            </CollapsibleSection>)}

            {/* ── Credit & Capacity ─────────────────────────────── */}
            {isFeatureEnabled('stakeholder_management', 'collateral.credit_capacity') && (
            <CollapsibleSection title="Credit & Capacity">

            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="credit_rating"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="Credit Rating" />}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="credit_rating_agency"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="Rating Agency" />}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="credit_rating_date"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Rating Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="total_exposure_limit"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Total Exposure Limit"
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                  />
                )}
              />
            </Grid>

            </CollapsibleSection>)}

            {/* ── Regulatory ───────────────────────────────────── */}
            {isFeatureEnabled('stakeholder_management', 'collateral.regulatory') && (
            <CollapsibleSection title="Regulatory">

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="registration_number"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="Registration Number" />}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="tax_number"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="Tax Number" />}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="license_number"
                control={control}
                defaultValue=""
                render={({ field }) => <TextField {...field} fullWidth label="License Number" />}
              />
            </Grid>

            </CollapsibleSection>)}

            {/* ── Capability ───────────────────────────────────── */}
            {isFeatureEnabled('stakeholder_management', 'collateral.capability') && (
            <CollapsibleSection title="Capability">

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="guarantee_fee_rate"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Guarantee Fee Rate"
                    type="number"
                    inputProps={{ min: 0, step: 0.0001 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="relationship_start_date"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Relationship Start Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name="notes"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Notes" multiline rows={3} />
                )}
              />
            </Grid>
            </CollapsibleSection>)}
          </>
        )}

        {/* ══════════════════════════════════════════════════════════
            NOTIFICATION PREFERENCES (investors + brokers)
            Investors render this as a sub-tab; brokers keep the accordion.
        ══════════════════════════════════════════════════════════ */}
        {(stakeholderType === 'investor' || stakeholderType === 'broker') && (
          stakeholderType === 'investor' ? (
            <SubTabSection active={isInvestorSubTabActive('notifications')}>
              <Grid size={12}>
                {renderNotificationPrefsContent()}
              </Grid>
            </SubTabSection>
          ) : (
          <CollapsibleSection title="Notification Preferences">
            <Grid size={12}>
              {renderNotificationPrefsContent()}
            </Grid>
          </CollapsibleSection>
          )
        )}
      </Grid>
      </fieldset>
    </form>
  );

  // ─── Dialog title label ───────────────────────────────────────────────────
  const issuerLabel = useTenant().terminology?.['stakeholder.issuer.label'] || 'Issuer';
  const typeLabel =
    stakeholderType === 'investor' ? 'Investor'
    : stakeholderType === 'broker' ? 'Broker'
    : stakeholderType === 'issuer' ? issuerLabel
    : 'Collateral Provider';

  const titleText = editingItem
    ? (readOnly ? typeLabel : `Edit ${typeLabel}`)
    : `Add ${typeLabel}`;

  return (
    <>
    <Dialog open={open} onClose={guardedClose} maxWidth={showTabs ? 'lg' : 'md'} fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1, gap: 1 }}>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="h6" component="div" noWrap>
            {(liveItem?.name || editingItem?.name) || titleText}
          </Typography>
          {editingItem && (liveItem?.type || editingItem?.type) && (
            <Typography variant="caption" color="text.secondary">
              {typeLabel} — {liveItem?.type || editingItem?.type}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {showTabs && readOnly && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEnterEditMode}
            >
              Edit
            </Button>
          )}
          {showTabs && !readOnly && (
            <>
              <Button size="small" onClick={handleCancelEdit} disabled={isSubmitting}>Cancel</Button>
              <Button
                size="small"
                type="submit"
                variant="contained"
                onClick={handleSubmit(onSubmit)}
                disabled={tab !== 0 || isSubmitting}
              >
                {isSubmitting ? 'Saving…' : 'Update'}
              </Button>
            </>
          )}
          <IconButton onClick={guardedClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {showTabs && (
          <Tabs
            value={tab}
            onChange={handleTabChange}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
          >
            <Tab label="Details" />
            {isIssuer ? (
              <Tab label={`Programs${programs?.length ? ` (${programs.length})` : ''}`} />
            ) : (
              <Tab label={`Contracts${contracts?.length ? ` (${contracts.length})` : ''}`} />
            )}
            <Tab label="Documents" />
          </Tabs>
        )}

        {(!showTabs || tab === 0) && (
          <>
            {loadingItem && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {renderDialogForm()}
          </>
        )}

        {showTabs && tab === 1 && isIssuer && (
          <Box sx={{ pt: 1 }}>
            {programsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : programsError ? (
              <Typography color="error" variant="body2">
                Failed to load programs: {programsError.message || String(programsError)}
              </Typography>
            ) : (
              <DataTable
                columns={programColumns}
                data={programs || []}
                emptyMessage="No programs found for this issuer"
                dense
              />
            )}
          </Box>
        )}

        {showTabs && tab === 1 && !isIssuer && (
          <Box sx={{ pt: 1 }}>
            {contractsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : contractsError ? (
              <Typography color="error" variant="body2">
                Failed to load contracts: {contractsError.message || String(contractsError)}
              </Typography>
            ) : (
              <DataTable
                columns={contractColumns}
                data={contracts || []}
                onRowClick={handleContractClick}
                emptyMessage="No contracts found for this stakeholder"
                dense
              />
            )}
          </Box>
        )}

        {showTabs && tab === 2 && (
          <Box sx={{ pt: 1 }}>
            {isIssuer ? (
              <StakeholderDocumentsTab
                mode="issuer"
                issuerId={stakeholderId}
                programs={programs || []}
              />
            ) : (
              <StakeholderDocumentsTab
                mode="investor"
                investorId={stakeholderId}
                contracts={contracts || []}
              />
            )}
          </Box>
        )}
      </DialogContent>
      {!showTabs && (
        <DialogActions>
          <Button onClick={guardedClose} disabled={isSubmitting}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isSubmitting ? 'Saving…' : (editingItem ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
    <ConfirmDialog {...confirm.dialogProps} />
    </>
  );
}

export default StakeholderFormDialog;
